import {
  CreateShippingManifestRequest,
  ManifestSearch,
  ShippingManifestDetail,
  ShippingManifestDocument,
  ShippingManifestSummary,
  ShippingReference,
  ShippingReferenceInput,
  SaveVesselJourneyRequest,
  SaveVesselPortRequest,
  SaveVesselLookupRequest,
  SaveFerryingPeriodRequest,
  SaveVesselPortCallRequest,
  SaveVesselRequest,
  UpdateShippingManifestRequest,
  VesselJourney,
  VesselMaster,
  VesselPortCall,
  VesselMovementData,
  VesselLookup,
  VesselPort,
  VesselPositionsData,
} from '@/types';
import {
  Core,
  VESSEL_AGENTS,
  VESSEL_BERTHS,
  VESSEL_JOURNEYS,
  VESSEL_LIST,
  VESSEL_MOVEMENTS,
  VESSEL_PORTS,
  VESSEL_PORT_CALLS,
  VESSEL_PORT_CALL_CLOSE,
  VESSEL_PORT_CALL_FERRYING,
  VESSEL_PORT_CALL_FERRYING_ITEM,
  VESSEL_PORT_CALL_READINESS,
  VESSEL_PORT_CALL_START,
  VESSEL_POSITIONS,
  VESSEL_MANIFEST,
  VESSEL_MANIFESTS,
  VESSEL_MANIFEST_HEADER,
  VESSEL_MANIFEST_ITEM,
  VESSEL_MANIFEST_DOCUMENTS,
  VESSEL_MANIFEST_DOCUMENT,
  VESSEL_MANIFEST_REFERENCES,
  VESSEL_MANIFEST_REFERENCE,
  VESSEL_DAMAGE_REPORT_DOWNLOAD,
} from '../Core';

class Vessel extends Core {
  getVesselMovements = async () =>
    this.get<VesselMovementData>(VESSEL_MOVEMENTS());

  getVesselPositions = async () =>
    this.get<VesselPositionsData>(VESSEL_POSITIONS());

  getVessels = async () => this.get<VesselMaster[]>(VESSEL_LIST());

  saveVessel = async (body: SaveVesselRequest) =>
    this.post<VesselMaster>(VESSEL_LIST(), body);

  getVesselPorts = async () => this.get<VesselPort[]>(VESSEL_PORTS());

  saveVesselPort = async (body: SaveVesselPortRequest) =>
    this.post<VesselPort>(VESSEL_PORTS(), body);

  getVesselAgents = async () => this.get<VesselLookup[]>(VESSEL_AGENTS());

  saveVesselAgent = async (body: SaveVesselLookupRequest) =>
    this.post<VesselLookup>(VESSEL_AGENTS(), body);

  getVesselBerths = async () => this.get<VesselLookup[]>(VESSEL_BERTHS());

  saveVesselBerth = async (body: SaveVesselLookupRequest) =>
    this.post<VesselLookup>(VESSEL_BERTHS(), body);

  getVesselJourneys = async (vesselId?: number) =>
    this.get<VesselJourney[]>(
      VESSEL_JOURNEYS(),
      vesselId ? { vesselId } : undefined,
    );

  saveVesselJourney = async (body: SaveVesselJourneyRequest) =>
    this.post<VesselJourney>(VESSEL_JOURNEYS(), body);

  getPortCalls = async (params?: {
    status?: string;
    siteId?: number;
    vesselId?: number;
    etaFrom?: string;
    etaTo?: string;
    shippingReference?: string;
  }) => this.get<VesselPortCall[]>(VESSEL_PORT_CALLS(), params);

  savePortCall = async (body: SaveVesselPortCallRequest) =>
    this.post<VesselPortCall>(VESSEL_PORT_CALLS(), body);

  saveFerryingPeriod = async (id: number, body: SaveFerryingPeriodRequest) =>
    this.post<VesselPortCall>(VESSEL_PORT_CALL_FERRYING(id), body);

  deleteFerryingPeriod = async (id: number, periodId: number) =>
    this.delete<VesselPortCall>(VESSEL_PORT_CALL_FERRYING_ITEM(id, periodId));

  completeReadiness = async (id: number, checkId: number, complete: boolean) =>
    this.post<VesselPortCall>(
      `${VESSEL_PORT_CALL_READINESS(id, checkId)}?complete=${complete}`,
    );

  startDischarge = async (id: number) =>
    this.post<VesselPortCall>(VESSEL_PORT_CALL_START(id));

  closePortCall = async (id: number) =>
    this.post<VesselPortCall>(VESSEL_PORT_CALL_CLOSE(id));

  postVesselManifest = async (documentFile: FormData) =>
    this.post(VESSEL_MANIFEST(), documentFile, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

  createShippingManifest = async (request: CreateShippingManifestRequest) => {
    const data = new FormData();
    data.append('manifestRef', request.manifestRef);
    if (request.eta) data.append('eta', request.eta);
    if (request.vesselId != null)
      data.append('vesselId', String(request.vesselId));
    if (request.vesselJourneyId != null)
      data.append('vesselJourneyId', String(request.vesselJourneyId));
    if (request.movementId != null)
      data.append('movementId', String(request.movementId));
    if (request.vesselName) data.append('vesselName', request.vesselName);
    if (request.voyage) data.append('voyage', request.voyage);
    if (request.customerId)
      data.append('customerId', String(request.customerId));
    if (request.siteId) data.append('siteId', String(request.siteId));
    if (request.notes) data.append('notes', request.notes);
    if (request.file) data.append('file', request.file);
    return this.post<ShippingManifestDetail>(VESSEL_MANIFEST_HEADER(), data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  getShippingManifests = async (search: ManifestSearch) =>
    this.get<ShippingManifestSummary[]>(VESSEL_MANIFESTS(), search);

  getShippingManifest = async (id: number) =>
    this.get<ShippingManifestDetail>(VESSEL_MANIFEST_ITEM(id));

  updateShippingManifest = async (
    id: number,
    body: UpdateShippingManifestRequest,
  ) => this.put<ShippingManifestDetail>(VESSEL_MANIFEST_ITEM(id), body);

  addShippingManifestReference = async (
    id: number,
    body: ShippingReferenceInput,
  ) => this.post<ShippingReference>(VESSEL_MANIFEST_REFERENCES(id), body);

  updateShippingManifestReference = async (
    id: number,
    referenceId: number,
    body: ShippingReferenceInput,
  ) =>
    this.put<ShippingReference>(
      VESSEL_MANIFEST_REFERENCE(id, referenceId),
      body,
    );

  deleteShippingManifestReference = async (id: number, referenceId: number) =>
    this.delete<boolean>(VESSEL_MANIFEST_REFERENCE(id, referenceId));

  deleteShippingManifestDocument = async (id: number, documentId: number) =>
    this.delete<boolean>(VESSEL_MANIFEST_DOCUMENT(id, documentId));

  addShippingManifestDocument = async (id: number, documentFile: FormData) =>
    this.post<ShippingManifestDocument>(
      VESSEL_MANIFEST_DOCUMENTS(id),
      documentFile,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );

  downloadShippingManifestDocument = async (id: number, documentId: number) =>
    this.get<Blob>(VESSEL_MANIFEST_DOCUMENT(id, documentId), undefined, {
      responseType: 'blob',
    });

  downloadShippingManifestByType = async (
    id: number,
    type: 'manifest' | 'vehicle-list',
  ) =>
    this.get<Blob>(
      `${VESSEL_MANIFEST_DOCUMENTS(id)}/download`,
      { type },
      { responseType: 'blob' },
    );

  downloadDamageReport = async (
    id: number,
    format: string,
    scope: string,
    scopeValue?: string,
  ) =>
    this.post<Blob>(
      VESSEL_DAMAGE_REPORT_DOWNLOAD(id, format),
      { scope, scopeValue },
      { responseType: 'blob' },
    );
}

export const vessel = new Vessel();
